import { Building2, ArrowRight, CheckCircle, MessageCircle, Phone, Mail, Shield, Users, BarChart3 } from "lucide-react"
import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { Label } from "@/components/ui/label"
import { Link } from "react-router-dom"
import { toast } from "@/components/ui/sonner"

const Demonstracao = () => {
  const [currentStep, setCurrentStep] = useState(1)
  const [formData, setFormData] = useState({
    email: "",
    name: "",
    whatsapp: "",
    help_type: "",
    role: "",
    agreed_privacy: false
  })

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const handleNext = () => {
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1)
    }
  }

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1)
    }
  }

  const handleSubmit = async () => {
    if (!formData.agreed_privacy) {
      toast.error("Você deve concordar com a política de privacidade")
      return
    }

    try {
      const response = await fetch('https://umnoiivuntnvsxkysvrr.supabase.co/functions/v1/send-demo-request', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      })

      if (response.ok) {
        toast.success("Solicitação enviada com sucesso! Em breve entraremos em contato.")
        
        // Reset form and go back to step 1
        setFormData({
          email: "",
          name: "",
          whatsapp: "",
          help_type: "",
          role: "",
          agreed_privacy: false
        })
        setCurrentStep(1)
      } else {
        const errorData = await response.json()
        toast.error(errorData.error || "Erro ao enviar solicitação. Tente novamente.")
      }
    } catch (error) {
      console.error('Erro ao enviar formulário:', error)
      toast.error("Erro ao enviar solicitação. Tente novamente.")
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-200">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center space-x-2">
              <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center">
                <Building2 className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">itaCondo</h1>
                <p className="text-xs text-gray-500">Gestão Residencial</p>
              </div>
            </Link>
            
            <div className="flex items-center space-x-4">
              <Link to="/">
                <Button variant="outline">Voltar ao Início</Button>
              </Link>
              <Link to="/login">
                <Button>Entrar</Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-5 gap-8 lg:gap-12 items-start">
            {/* Left Side - Info */}
            <div className="lg:col-span-3 space-y-6 lg:space-y-8">
              <div>
                <h1 className="text-3xl lg:text-4xl xl:text-5xl font-bold text-gray-900 mb-4 lg:mb-6">
                  Revolucione a gestão do seu condomínio!
                </h1>
                <p className="text-lg lg:text-xl text-gray-600 leading-relaxed">
                  Descubra como o itaCondo pode transformar completamente a administração do seu condomínio 
                  com tecnologia de ponta e simplicidade incomparável.
                </p>
              </div>

              <div className="grid sm:grid-cols-2 gap-4 lg:gap-6">
                <div className="bg-white rounded-xl p-4 lg:p-6 shadow-md border border-blue-100">
                  <div className="flex items-center mb-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                      <Shield className="h-5 w-5 text-blue-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Segurança Total</h3>
                  </div>
                  <p className="text-sm text-gray-600">
                    Controle rigoroso de acessos, visitantes e registros com tecnologia avançada.
                  </p>
                </div>

                <div className="bg-white rounded-xl p-4 lg:p-6 shadow-md border border-green-100">
                  <div className="flex items-center mb-3">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mr-3">
                      <Users className="h-5 w-5 text-green-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Interface Intuitiva</h3>
                  </div>
                  <p className="text-sm text-gray-600">
                    Sistema fácil de usar para síndicos, porteiros, moradores e administradores.
                  </p>
                </div>

                <div className="bg-white rounded-xl p-4 lg:p-6 shadow-md border border-purple-100">
                  <div className="flex items-center mb-3">
                    <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mr-3">
                      <BarChart3 className="h-5 w-5 text-purple-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Relatórios Completos</h3>
                  </div>
                  <p className="text-sm text-gray-600">
                    Análises detalhadas para tomada de decisões estratégicas e inteligentes.
                  </p>
                </div>

                <div className="bg-white rounded-xl p-4 lg:p-6 shadow-md border border-orange-100">
                  <div className="flex items-center mb-3">
                    <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center mr-3">
                      <CheckCircle className="h-5 w-5 text-orange-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Suporte Dedicado</h3>
                  </div>
                  <p className="text-sm text-gray-600">
                    Equipe especializada pronta para ajudar você a qualquer momento.
                  </p>
                </div>
              </div>

              <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
                <h3 className="text-xl font-bold text-blue-900 mb-3">
                  🚀 Por que escolher o itaCondo?
                </h3>
                <ul className="space-y-2 text-blue-800">
                  <li className="flex items-center">
                    <CheckCircle className="h-4 w-4 mr-2 text-blue-600" />
                    Elimina papéis e processos manuais
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="h-4 w-4 mr-2 text-blue-600" />
                    Reduz custos operacionais significativamente
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="h-4 w-4 mr-2 text-blue-600" />
                    Melhora comunicação entre moradores
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="h-4 w-4 mr-2 text-blue-600" />
                    Aumenta transparência na gestão
                  </li>
                </ul>
              </div>
            </div>

            {/* Right Side - Form */}
            <div className="lg:col-span-2">
              <div className="lg:sticky lg:top-24">
                <Card className="shadow-xl border-0 bg-blue-600 text-white">
                  <CardContent className="p-6">
                    <div className="mb-6">
                      <div className="flex justify-between text-sm mb-3">
                        <span className="font-medium">Passo {currentStep} de 3</span>
                        <span className="opacity-80">{Math.round((currentStep / 3) * 100)}%</span>
                      </div>
                      <div className="w-full bg-blue-500 rounded-full h-2">
                        <div 
                          className="bg-white rounded-full h-2 transition-all duration-500 ease-out"
                          style={{ width: `${(currentStep / 3) * 100}%` }}
                        ></div>
                      </div>
                    </div>

                    {/* Step 1 - Contact Info */}
                    {currentStep === 1 && (
                      <div className="space-y-5">
                        <div>
                          <h3 className="text-xl font-bold mb-2">Vamos começar!</h3>
                          <p className="text-blue-100 text-sm">Preencha seus dados para continuarmos</p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium mb-2">
                            Seu email <span className="text-blue-200">*</span>
                          </label>
                          <Input
                            type="email"
                            value={formData.email}
                            onChange={(e) => handleInputChange('email', e.target.value)}
                            placeholder="seu@email.com"
                            className="bg-white text-gray-900 border-0 h-12"
                            required
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium mb-2">
                            Nome completo <span className="text-blue-200">*</span>
                          </label>
                          <Input
                            value={formData.name}
                            onChange={(e) => handleInputChange('name', e.target.value)}
                            placeholder="Seu nome completo"
                            className="bg-white text-gray-900 border-0 h-12"
                            required
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium mb-2">
                            WhatsApp <span className="text-blue-200">*</span>
                          </label>
                          <Input
                            value={formData.whatsapp}
                            onChange={(e) => handleInputChange('whatsapp', e.target.value)}
                            placeholder="(21) 99999-9999"
                            className="bg-white text-gray-900 border-0 h-12"
                          />
                          <p className="text-blue-200 text-xs mt-1">Inclua o DDD: (11) 99999-9999</p>
                        </div>

                        <Button 
                          onClick={handleNext}
                          className="w-full bg-white text-blue-600 hover:bg-blue-50 h-12 text-base font-semibold"
                          disabled={!formData.email || !formData.name || !formData.whatsapp}
                        >
                          Continuar
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                      </div>
                    )}

                    {/* Step 2 - Help Type */}
                    {currentStep === 2 && (
                      <div className="space-y-5">
                        <div>
                          <h3 className="text-xl font-bold mb-2">
                            Como podemos ajudar você?
                          </h3>
                          <p className="text-blue-100 text-sm mb-4">
                            Selecione a opção que melhor descreve sua necessidade:
                          </p>
                        </div>

                        <RadioGroup value={formData.help_type} onValueChange={(value) => handleInputChange('help_type', value)} className="space-y-3">
                          <div className="flex items-start space-x-3 bg-white/10 p-4 rounded-lg hover:bg-white/20 transition-colors cursor-pointer">
                            <RadioGroupItem value="support" id="support" className="border-white text-white mt-1" />
                            <Label htmlFor="support" className="cursor-pointer leading-tight">
                              <span className="font-medium">Sou cliente atual</span><br />
                              <span className="text-blue-100 text-sm">Preciso de ajuda com boletos, relatórios ou questões técnicas</span>
                            </Label>
                          </div>
                          
                          <div className="flex items-start space-x-3 bg-white/10 p-4 rounded-lg hover:bg-white/20 transition-colors cursor-pointer">
                            <RadioGroupItem value="demo" id="demo" className="border-white text-white mt-1" />
                            <Label htmlFor="demo" className="cursor-pointer leading-tight">
                              <span className="font-medium">Quero conhecer o itaCondo</span><br />
                              <span className="text-blue-100 text-sm">Interessado em preços, funcionalidades ou demonstração</span>
                            </Label>
                          </div>
                          
                          <div className="flex items-start space-x-3 bg-white/10 p-4 rounded-lg hover:bg-white/20 transition-colors cursor-pointer">
                            <RadioGroupItem value="other" id="other" className="border-white text-white mt-1" />
                            <Label htmlFor="other" className="cursor-pointer leading-tight">
                              <span className="font-medium">Outro assunto</span><br />
                              <span className="text-blue-100 text-sm">Parceria, integração ou outras questões</span>
                            </Label>
                          </div>
                        </RadioGroup>

                        <div className="flex gap-3">
                          <Button 
                            onClick={handlePrevious}
                            variant="outline"
                            className="flex-1 bg-transparent border-white text-white hover:bg-white hover:text-blue-600 h-12"
                          >
                            Voltar
                          </Button>
                          <Button 
                            onClick={handleNext}
                            className="flex-1 bg-white text-blue-600 hover:bg-blue-50 h-12 font-semibold"
                            disabled={!formData.help_type}
                          >
                            Continuar
                            <ArrowRight className="ml-2 h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* Step 3 - Role */}
                    {currentStep === 3 && (
                      <div className="space-y-5">
                        <div>
                          <h3 className="text-xl font-bold mb-2">
                            Qual seu papel no condomínio?
                          </h3>
                          <p className="text-blue-100 text-sm mb-4">
                            Isso nos ajuda a personalizar nossa abordagem:
                          </p>
                        </div>

                        <RadioGroup value={formData.role} onValueChange={(value) => handleInputChange('role', value)} className="space-y-3">
                          <div className="flex items-center space-x-3 bg-white/10 p-3 rounded-lg hover:bg-white/20 transition-colors cursor-pointer">
                            <RadioGroupItem value="sindico" id="sindico" className="border-white text-white" />
                            <Label htmlFor="sindico" className="cursor-pointer font-medium">Síndico(a)</Label>
                          </div>
                          
                          <div className="flex items-center space-x-3 bg-white/10 p-3 rounded-lg hover:bg-white/20 transition-colors cursor-pointer">
                            <RadioGroupItem value="administradora" id="administradora" className="border-white text-white" />
                            <Label htmlFor="administradora" className="cursor-pointer font-medium">Administradora de Condomínios</Label>
                          </div>
                          
                          <div className="flex items-center space-x-3 bg-white/10 p-3 rounded-lg hover:bg-white/20 transition-colors cursor-pointer">
                            <RadioGroupItem value="morador" id="morador" className="border-white text-white" />
                            <Label htmlFor="morador" className="cursor-pointer font-medium">Morador(a)</Label>
                          </div>
                          
                          <div className="flex items-center space-x-3 bg-white/10 p-3 rounded-lg hover:bg-white/20 transition-colors cursor-pointer">
                            <RadioGroupItem value="porteiro" id="porteiro" className="border-white text-white" />
                            <Label htmlFor="porteiro" className="cursor-pointer font-medium">Porteiro/Funcionário</Label>
                          </div>
                          
                          <div className="flex items-center space-x-3 bg-white/10 p-3 rounded-lg hover:bg-white/20 transition-colors cursor-pointer">
                            <RadioGroupItem value="outros" id="outros" className="border-white text-white" />
                            <Label htmlFor="outros" className="cursor-pointer font-medium">Outros</Label>
                          </div>
                        </RadioGroup>

                        <div className="bg-white/10 p-4 rounded-lg">
                          <div className="flex items-start space-x-3">
                            <input 
                              type="checkbox" 
                              id="privacy" 
                              checked={formData.agreed_privacy}
                              onChange={(e) => handleInputChange('agreed_privacy', e.target.checked)}
                              className="mt-1 w-4 h-4"
                            />
                            <Label htmlFor="privacy" className="text-sm cursor-pointer leading-tight">
                              Li e concordo com a{' '}
                              <Link to="/politica-privacidade" className="underline hover:text-blue-200 font-medium">
                                Política de Privacidade
                              </Link>
                              {' '}do itaCondo.
                            </Label>
                          </div>
                        </div>

                        <div className="flex gap-3">
                          <Button 
                            onClick={handlePrevious}
                            variant="outline"
                            className="flex-1 bg-transparent border-white text-white hover:bg-white hover:text-blue-600 h-12"
                          >
                            Voltar
                          </Button>
                          <Button 
                            onClick={handleSubmit}
                            className="flex-1 bg-white text-blue-600 hover:bg-blue-50 h-12 font-semibold"
                            disabled={!formData.role || !formData.agreed_privacy}
                          >
                            Finalizar
                            <CheckCircle className="ml-2 h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* WhatsApp Float Button */}
      <div className="fixed bottom-6 right-6 z-50">
        <Button
          onClick={() => window.open('https://wa.me/5521998231720', '_blank')}
          className="bg-whatsapp text-whatsapp-foreground hover:bg-whatsapp/90 p-4 rounded-full shadow-lg hover:shadow-xl transition-all duration-300"
        >
          <MessageCircle className="h-6 w-6" />
        </Button>
      </div>
    </div>
  )
}

export default Demonstracao